import { useTranslation } from 'react-i18next'
import useCentralizedChatManager from '@/components/useCentralizedChatManager'
import { useCallback, useRef } from 'react'
import { env } from '@/services/EnvironmentVariables'
import ElectronIPC from '@/services/ElectronIPC'
import { Button } from '@/UIComponents2/Button'
import PlusFilledIcon from '@/CustomIcons/PlusFilledIcon'
import { Field } from '@ark-ui/react'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'
import { ControlButton } from '@/UIComponents2/ControlButton'
import ChatSendIcon from '@/CustomIcons/ChatSendIcon'
import { SidebarTooltip } from '@/pages/SessionPage/internal/sidebar/SidebarTooltip'

export default function ChatTextInputSection({
    chatManager,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
}) {
    const { t } = useTranslation()
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const { logUserActivityWithContext } = useAnalytics()

    const sendMessageHandler = useCallback(
        (text: string) => {
            if (chatManager == null) {
                throw new Error(`Chatbox: chatManager is null.`)
            }

            if (text.trim().length > 0) {
                chatManager.sendText(text)
            }

            // TODO: restore
            // logUserActivityWithContext(AnalyticsEvents.SENT_CHAT_MESSAGE)
        },
        [chatManager]
    )

    const onKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            if (e.shiftKey) {
                try {
                    // Insert newline at cursor position
                    const textarea = e.target
                    const start = textarea.selectionStart
                    const end = textarea.selectionEnd
                    const value = textarea.value

                    // Insert newline at cursor position
                    const newValue = value.substring(0, start) + '\n' + value.substring(end)
                    textarea.value = newValue

                    // Set cursor position after the inserted newline
                    const newCursorPosition = start + 1
                    textarea.setSelectionRange(newCursorPosition, newCursorPosition)
                } catch (err) {
                    e.target.value += '\n'
                }
            } else {
                sendMessageHandler(e.target.value)
                e.target.value = ''
            }
        }
    }

    const onContextMenu = (e: any) => {
        if (env.IS_ELECTRON) {
            e.preventDefault()
            const selection = window?.getSelection()?.toString() ?? ''
            ElectronIPC.showContextMenu({
                cut: selection.length > 0,
                copy: selection.length > 0,
                paste: true,
            })
        }
    }

    const handleSendButtonClick = () => {
        if (textareaRef.current) {
            sendMessageHandler(textareaRef.current.value)
            textareaRef.current.value = ''
        }
    }

    const onAttachFileHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!chatManager) {
            return
        }

        if (!e.target.files) {
            return
        }

        const files: File[] = Array.from(e.target.files)
        // NOTE: for now, only support uploading one file at a time. The open file dialog
        // from <input> only allows you to select one file anyway.
        if (files && files[0]) {
            chatManager.sendFile(files)
        }

        logUserActivityWithContext(AnalyticsEvents.ATTACHED_CHAT_FILE, {
            filename: files[0]?.name,
            fileSize: files[0]?.size,
        })
    }

    const fileInputRef = useRef<HTMLInputElement>(null)

    return (
        <div className="w-full grid grid-cols-[min-content_1fr_min-content] items-center gap-2 px-2 py-[7px] bg-white/10 rounded-[20px] shadow-glow">
            <SidebarTooltip text={t('Attach file')} noNativeWrapper>
                <Button
                    variant="basic"
                    className="ml-1 hover:text-white hover:scale-115 transition-transform duration-150"
                    onClick={() => {
                        if (fileInputRef.current) {
                            fileInputRef.current.click()
                        }
                    }}
                >
                    <PlusFilledIcon />
                    <input ref={fileInputRef} type="file" hidden onChange={onAttachFileHandler} />
                </Button>
            </SidebarTooltip>

            <Field.Textarea
                ref={textareaRef}
                className="w-full placeholder:text-muse-fg/50 py-1 p-2 rounded-[12px]"
                style={{ outline: 'none' }}
                placeholder={t('Write a message...') || ''}
                rows={1}
                autoresize
                onKeyDown={onKeyDown}
                onContextMenu={onContextMenu}
            />

            <div className="">
                <SidebarTooltip text={t('Send Message')}>
                    <ControlButton className="p-2" variant="glow" onClick={handleSendButtonClick}>
                        <ChatSendIcon style={{ fontSize: '1.1rem' }} />
                    </ControlButton>
                </SidebarTooltip>
            </div>
        </div>
    )
}
